import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Send } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INITIAL_FORM_STATE = {
  name: '',
  organization: '',
  role: '',
  email: '',
  phone: '',
  location: '',
  inquiryType: 'general',
  message: ''
};

const INQUIRY_TYPES = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'fiber-splicing', label: 'Fiber Splicing Services' },
  { value: 'tower-deployment', label: 'Tower Site Deployment' },
  { value: 'field-verification', label: 'Field Verification & QC' },
  { value: 'education', label: 'Education Seminar' },
  { value: 'advisory', label: 'Advisory Services' },
  { value: 'partnership', label: 'Partnership Discussion' }
];

const FormField = ({ label, required, children }) => (
  <div className="space-y-2">
    <Label className="text-[#f1f5f9]">
      {label} {required && '*'}
    </Label>
    {children}
  </div>
);

const ContactForm = () => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/send-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Inquiry submitted successfully', {
          description: "We'll review your request within 1–2 business days."
        });
        setFormData(INITIAL_FORM_STATE);
      } else {
        toast.error('Failed to submit inquiry', {
          description: data.detail || 'Please try again later.'
        });
      }
    } catch {
      toast.error('Failed to submit inquiry', {
        description: 'Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClassName = "bg-[#0f1d32] border-[#1e3a5f] text-[#f1f5f9] placeholder:text-[#64748b] focus:border-[#3b82f6] focus:ring-[#3b82f6]";

  return (
    <Card className="bg-[#162544] border-[#1e3a5f]/50 rounded-2xl overflow-hidden">
      <CardHeader className="bg-[#1e3a5f]/30 border-b border-[#1e3a5f]/50 p-8">
        <CardTitle className="text-2xl text-[#f1f5f9]">Submit Your Inquiry</CardTitle>
        <p className="text-sm mt-2 text-[#94a3b8]">
          Complete the form below to discuss services, seminars, or partnership opportunities.
        </p>
      </CardHeader>
      <CardContent className="p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="Full Name" required>
              <Input
                name="name"
                data-testid="contact-name-input"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="John Doe"
                className={inputClassName}
              />
            </FormField>

            <FormField label="Email Address" required>
              <Input
                name="email"
                type="email"
                data-testid="contact-email-input"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="john@example.com"
                className={inputClassName}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="Organization" required>
              <Input
                name="organization"
                data-testid="contact-organization-input"
                value={formData.organization}
                onChange={handleChange}
                required
                placeholder="Company or Institution Name"
                className={inputClassName}
              />
            </FormField>

            <FormField label="Role or Title" required>
              <Input
                name="role"
                data-testid="contact-role-input"
                value={formData.role}
                onChange={handleChange}
                required
                placeholder="e.g., Network Engineer, Director"
                className={inputClassName}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="Phone Number (Optional)">
              <Input
                name="phone"
                type="tel"
                data-testid="contact-phone-input"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
                className={inputClassName}
              />
            </FormField>

            <FormField label="Project Location (Optional)">
              <Input
                name="location"
                data-testid="contact-location-input"
                value={formData.location}
                onChange={handleChange}
                placeholder="City, State/Country"
                className={inputClassName}
              />
            </FormField>
          </div>

          <FormField label="Inquiry Type" required>
            <select
              name="inquiryType"
              data-testid="contact-inquiry-type-select"
              value={formData.inquiryType}
              onChange={handleChange}
              required
              className="w-full h-10 px-3 rounded-md text-sm bg-[#0f1d32] border border-[#1e3a5f] text-[#f1f5f9] focus:outline-none focus:ring-2 focus:ring-[#3b82f6] focus:border-[#3b82f6]"
            >
              {INQUIRY_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </FormField>

          <FormField label="Project Overview or Inquiry Description" required>
            <Textarea
              name="message"
              data-testid="contact-message-textarea"
              value={formData.message}
              onChange={handleChange}
              required
              placeholder="Please describe your project requirements, timeline considerations, or any questions you may have..."
              rows={6}
              className={`resize-none ${inputClassName}`}
            />
          </FormField>

          <Button
            type="submit"
            data-testid="contact-submit-button"
            disabled={isSubmitting}
            className="w-full btn-primary text-white font-medium py-6 text-lg rounded-xl"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
            <Send className="ml-2" size={18} />
          </Button>

          <p className="text-xs text-center text-[#64748b]">
            * Required fields. Your information will be reviewed within 1–2 business days.
          </p>
        </form>
      </CardContent>
    </Card>
  );
};

export default ContactForm;
